// @flow
import { createSelector } from 'reselect';
import { filter } from 'ramda';

import * as intervals from '../utils/intervals';
import {
  expandIntervalByMonths,
  expandIntervalByYears,
  intervalLengthMonths,
  intervalInside,
} from '../utils/time';
import { timespanPresets } from '../utils/timePresets';
import { Precision, Compare } from '../constants';
import { getSafeTimeBounds } from '../selectors/timeBounds';
import {
  getTimespan,
  getCustomTimespanInterval,
  getPrecision,
  getCompare,
  getCustomCompareInterval,
} from './options';

/*
 * Selected timespan
 */
export const getTotalTimespanInterval = createSelector(
  getTimespan,
  getSafeTimeBounds,
  getCustomTimespanInterval,
  intervals.totalTimespanInterval,
);

/*
 * Selected timespan, expanded by precision
 */
export const getDetailedTimespanInterval = createSelector(
  getPrecision,
  getTotalTimespanInterval,
  (precision, singleInterval) =>
    singleInterval
      ? precision === Precision.MONTHLY
        ? expandIntervalByMonths(singleInterval)
        : expandIntervalByYears(singleInterval)
      : [],
);

/*
 * Array of intervals to compare timespan to
 */
export const getCompareIntervals = createSelector(
  getTotalTimespanInterval,
  getCompare,
  getCustomCompareInterval,
  getSafeTimeBounds,
  intervals.compareIntervals,
);

/*
 * Min and max date in the array of compare timespans
 */
export const getTotalCompareInterval = createSelector(
  getCompareIntervals,
  intervals.totalCompareInterval,
);

/*
 * Time intervals that actually go into the API call
 */
export const getApiIntervals = createSelector(
  getCompare,
  getDetailedTimespanInterval,
  getTotalTimespanInterval,
  getCompareIntervals,
  (
    compare,
    detailedTimespanInterval,
    singleTimespanInterval,
    compareIntervals,
  ) =>
    compare === Compare.NONE
      ? detailedTimespanInterval
      : [singleTimespanInterval, ...compareIntervals],
);

export const getAvailableTimespans = createSelector(
  getSafeTimeBounds,
  timeBounds =>
    timeBounds
      ? Object.keys(
          filter(
            presetFn => intervalInside(presetFn(timeBounds), timeBounds),
            timespanPresets,
          ),
        )
      : Object.keys(timespanPresets),
);

export const getAvailableCompare = createSelector(
  getSafeTimeBounds,
  getTimespan,
  getCustomTimespanInterval,
  intervals.availableCompare,
);

export const isSingleInterval = createSelector(
  getTotalTimespanInterval,
  getPrecision,
  (interval, precision) =>
    interval
      ? intervalLengthMonths(interval) <=
        (precision === Precision.MONTHLY ? 1 : 12)
      : null,
);



// WEBPACK FOOTER //
// ./src/selectors/intervals.js